<!-- Under /Videos' orange "How do I unlock…" bar: what this viewer can watch, as the video
     page's row of dark buttons (VideoAccessActionButton, Video/Bespoke.vue); they replace the
     icon legend (2026-10-21, Seán): Unlocked (what this viewer can watch) · Locked (what they
     can't yet). Each with its count of videos, as the heading's; a category with none is left out.
     A press shows only that category's videos (/Videos?unlocked or ?locked), again shows all. Phones: labels only, to keep to one row. -->
<template>
  <!-- only while some videos are locked for this viewer (2026-10-21, Seán; VIDEOS.md) -->
  <div v-if="hasLocked" class="access-categories">
    <VideoAccessActionButton
      v-for="category in categories"
      :key="category.key"
      :label="category.label"
      :aria-pressed="category.key === selected"
      @click="emit('select', category.key === selected ? '' : category.key)">
      <span>{{ category.label }}</span><Count :count="category.count" />
    </VideoAccessActionButton>
  </div>
</template>

<script setup>
import {computed} from "vue";
import Count from "@/components/Count.vue";
import VideoAccessActionButton from "@/components/Video/VideoAccessActionButton.vue";
import {getAccessFilter} from "@/components/Video/videoCards";
import useUserSafe from "@/composables/useUserSafe";
import useVideoAccess from "@/composables/useVideoAccess";
const props = defineProps({
  showcases: {type: Object, default: () => ({})},
  selected: {type: String, default: ""} // the category shown alone
});
const emit = defineEmits(["select"]);

const LABELS = {unlocked: "Unlocked", locked: "Locked"};

function getCounts(showcases, viewer) {
  const counts = {unlocked: 0, locked: 0};
  for (const [showcase, {videos = {}}] of Object.entries(showcases)) {
    for (const [hash, video] of Object.entries(videos)) {
      counts[getAccessFilter({video, hash, showcase, ...viewer})]++;
    }
  }
  return counts;
}

const {isSignedIn, user} = useUserSafe();
useVideoAccess(); // reads the account again when the page comes back into view
const counts = computed(() => {
  const viewer = {user: user.value, isSignedIn: isSignedIn.value};
  return getCounts(props.showcases, viewer);
});
const hasLocked = computed(() => counts.value.locked > 0);
const categories = computed(() => {
  return Object.keys(LABELS)
    .filter((key) => counts.value[key] > 0)
    .map((key) => ({key, label: LABELS[key], count: counts.value[key]}));
});
</script>

<style scoped>
/* as the video page's row (Bespoke.vue .access-inline-buttons): the full width, shared equally,
   with room between the buttons; a button wraps to its own row only when its label won't fit */
.access-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
}
.access-categories > * {
  width: auto; /* the button's own 100% is for a lone button */
  flex: 1 1 0;
  min-width: max-content;
  white-space: nowrap;
}
/* as the Year and Session menus (DropdownRoutes/Dropdown.vue), in the page's filter colours
   (views/Videos/index.vue --filter-*): neutral whether or not another filter is on; chosen:
   VideoAccessActionButton.vue's green. All the same height and border */
.access-categories .access-action-btn {
  min-height: var(--filter-height, 2.5rem);
  font-weight: var(--filter-weight, 600);
  border: 1px solid var(--filter-border, #d3d3d3);
  border-radius: 0.5rem;
  box-shadow: 0 0.13rem 0.13rem rgba(0, 0, 0, 0.2);
}
.access-categories .access-action-btn:not([aria-pressed="true"]) {
  color: var(--filter-text, black);
  background: var(--filter-background, white);
}
/* hover: a darker border only, so unchosen buttons keep one background (the one just pressed
   to clear a filter is still under the pointer); not on touch screens, where hover sticks */
@media (hover: hover) {
  .access-categories .access-action-btn:not([aria-pressed="true"]):hover {
    background: var(--filter-background, white);
    border-color: var(--filter-muted, #888);
  }
}
.access-categories .access-action-btn[aria-pressed="true"] {
  border-color: var(--brand-color);
}
/* the count (Count.vue), in the label's orange */
.count {
  margin-left: 0.4em;
}
/* phones: still equal widths on one row, tighter; no counts, and a long label wraps inside its
   button (e.g. "Requires / purchase") rather than widening it */
@media (max-width: 560px) {
  .access-categories {
    gap: 8px;
  }
  .access-categories > * {
    min-width: 0;
    white-space: normal;
    line-height: 1.2;
    padding-inline: 0.5rem;
  }
  .count {
    display: none;
  }
}
</style>
